import { useState, type JSX } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { KINGDOMS, QUESTS } from '@yuan-math/content';
import type { LearnerProfile } from '@yuan-math/learning';
import {
  blockingQuest,
  questProgress,
  type QuestDef,
  type QuestObjective,
  type QuestProgress,
  type StoryBeat,
} from '@yuan-math/world';
import { Button, Card, Confetti } from '@yuan-math/ui';
import { registry, useLearner } from '../../state/LearnerContext.tsx';
import { useRewards } from '../../state/RewardsContext.tsx';
import { MasteryBand } from '../../components/MasteryBand.tsx';
import { QUEST_KIND, QuestKindBadge, QuestStateChip } from '../../components/QuestBoard.tsx';
import { kingdomSealed } from '../../components/kingdomSeal.ts';
import { NotFoundPage } from '../NotFoundPage.tsx';

/**
 * `/adventure/quests/:questId` — one quest: the story, the objective, and the
 * work (docs/rpg.md §13–14, §21).
 *
 * **There is no exercise runner here, and there must never be one.** A quest
 * owns no mathematics of its own: `questProgress` derives everything from the
 * ordinary attempt log, the ordinary completed-lesson list and ordinary
 * mastery, so a student who never opens this page and simply practises still
 * finishes the quest. What this page adds is a *reason* and a *link*, and the
 * links go to `/practice/:conceptId` and `/lessons/:id` — the same pages the
 * dashboard, the map and the course pages send people to. Forking
 * `ExerciseRunner` for a quest would create a second place answers are graded,
 * and within a release the two would disagree about something.
 *
 * The story is skippable in one tap, per §21 ("NPC dialogue should be short and
 * skippable. Do not turn every lesson into a long story"), and the objective,
 * the progress bar and the work are all below it — so a student who never
 * expands a word of narrative loses nothing at all.
 */

/**
 * Which quests the reader has already skipped past.
 *
 * Module state, deliberately, and nothing in the profile: whether somebody
 * fancies re-reading a lamplighter's dialogue is not a fact about their
 * mathematics, and has no business being persisted, merged between devices or
 * pushed to a server. It lives exactly as long as the tab does — which is what
 * "remembered for the session" means here. Keyed by quest, so skipping one
 * story does not silently skip the next quest's.
 */
const storySkipped = new Map<string, boolean>();

/* -------------------------------------------------------------------------- */
/* Saying the objective in plain words                                        */
/* -------------------------------------------------------------------------- */

/** "Counting, Place Value or Rounding" — an Oxford-comma-free spoken list. */
function spokenList(items: string[]): string {
  if (items.length <= 1) return items[0] ?? '';
  return `${items.slice(0, -1).join(', ')} or ${items[items.length - 1]}`;
}

const conceptTitle = (id: string): string => registry.concept(id)?.title ?? id;

/**
 * The objective, stated the way a person would state it.
 *
 * Deliberately not "objective: practice ×6". The student is being asked to do
 * something specific and should be able to read what it is without learning the
 * platform's vocabulary first.
 */
function objectiveSentence(objective: QuestObjective): string {
  switch (objective.kind) {
    case 'practice': {
      const count = objective.correctCount;
      return `Answer ${count} ${count === 1 ? 'problem' : 'problems'} correctly on ${spokenList(
        objective.conceptIds.map(conceptTitle),
      )}.`;
    }
    case 'master-concepts': {
      const total = objective.conceptIds.length;
      return objective.count >= total
        ? `Reach mastery on ${total === 1 ? 'this skill' : `all ${total} of these skills`}.`
        : `Reach mastery on ${objective.count} of these ${total} skills — any ${objective.count}.`;
    }
    case 'complete-lessons': {
      const count = objective.lessonIds.length;
      return count === 1 ? 'Read this lesson.' : `Read these ${count} lessons.`;
    }
    case 'clear-quests': {
      const count = objective.questIds.length;
      return `See ${count === 1 ? 'this quest' : `all ${count} of these quests`} through to the end, reward claimed.`;
    }
  }
}

/* -------------------------------------------------------------------------- */
/* The story                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * One beat, as an NPC panel (§21). A beat with no speaker is narration and gets
 * no name plate — inventing a speaker for it would put words in somebody's
 * mouth that `content/` never wrote.
 */
function StoryPanel({ beat }: { beat: StoryBeat }): JSX.Element {
  return (
    <li className={`quest-beat ${beat.speaker ? 'quest-beat--spoken' : 'quest-beat--narration'}`}>
      {beat.speaker ? (
        <p className="quest-beat__speaker">
          <span className="quest-beat__avatar" aria-hidden="true">
            🧑‍🌾
          </span>
          {beat.speaker}
        </p>
      ) : null}
      <p className="quest-beat__text">{beat.text}</p>
    </li>
  );
}

/* -------------------------------------------------------------------------- */
/* The work                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * What to go and do, as links to pages that already exist.
 *
 * Two links per concept on purpose: practice is the thing the quest counts, and
 * the concept page is where a student who cannot yet do it should go first. A
 * quest that only offered "practise" would be sending a stuck child back to the
 * same wall.
 */
function ConceptTargets({
  conceptIds,
  showBand,
  masteryOf,
}: {
  conceptIds: string[];
  showBand: boolean;
  masteryOf: (id: string) => { level: Parameters<typeof MasteryBand>[0]['level'] };
}): JSX.Element {
  return (
    <ul className="quest-targets">
      {conceptIds.map((conceptId) => {
        const concept = registry.concept(conceptId);
        if (!concept) return null;
        // A planned concept has a page but no lessons or exercises behind it,
        // so offering "practise" would be a link to an empty room.
        const planned = concept.status === 'planned';

        return (
          <li key={conceptId} className="quest-target">
            <div className="quest-target__body">
              <Link to={`/concepts/${conceptId}`} className="quest-target__name">
                {concept.title}
              </Link>
              {showBand && !planned ? <MasteryBand level={masteryOf(conceptId).level} /> : null}
              {planned ? <span className="quest-target__horizon">On the horizon</span> : null}
            </div>
            {planned ? null : (
              <Link to={`/practice/${conceptId}`} className="btn btn--sky btn--sm">
                Practise
              </Link>
            )}
          </li>
        );
      })}
    </ul>
  );
}

function LessonTargets({ lessonIds, read }: { lessonIds: string[]; read: ReadonlySet<string> }): JSX.Element {
  return (
    <ul className="quest-targets">
      {lessonIds.map((lessonId) => {
        const lesson = registry.lesson(lessonId);
        if (!lesson) return null;
        const done = read.has(lessonId);

        return (
          <li key={lessonId} className="quest-target">
            <div className="quest-target__body">
              <Link to={`/lessons/${lessonId}`} className="quest-target__name">
                {lesson.title}
              </Link>
              {/* A word and a tick, never a tick alone. */}
              {done ? (
                <span className="quest-target__done">
                  <span aria-hidden="true">✓</span> Read
                </span>
              ) : null}
            </div>
            <Link to={`/lessons/${lessonId}`} className="btn btn--sky btn--sm">
              {done ? 'Read again' : 'Open lesson'}
            </Link>
          </li>
        );
      })}
    </ul>
  );
}

function QuestTargets({
  questIds,
  profile,
  now,
}: {
  questIds: string[];
  profile: LearnerProfile;
  now: number;
}): JSX.Element {
  return (
    <ul className="quest-targets">
      {questIds.map((questId) => {
        const def = QUESTS.find((candidate) => candidate.id === questId);
        if (!def) return null;
        const progress = questProgress(def, QUESTS, profile, now);

        return (
          <li key={questId} className="quest-target">
            <div className="quest-target__body">
              <Link to={`/adventure/quests/${questId}`} className="quest-target__name">
                {def.title}
              </Link>
              <QuestStateChip progress={progress} blocker={blockingQuest(def, QUESTS, profile)} />
            </div>
          </li>
        );
      })}
    </ul>
  );
}

/* -------------------------------------------------------------------------- */
/* The page                                                                   */
/* -------------------------------------------------------------------------- */

export function QuestPage(): JSX.Element {
  const { questId } = useParams();
  const { profile, masteryOf, acceptQuest, claimQuestReward } = useLearner();
  const { celebrate } = useRewards();
  // Bumped when the story is toggled: `storySkipped` is the store, and reading
  // it during render keeps the two quests' answers apart even though React
  // reuses this component when the route parameter changes.
  const [, redraw] = useState(0);
  // Which quest was claimed *on this screen*, so the confetti fires once for
  // the act rather than every time a claimed quest is re-opened.
  const [claimedHere, setClaimedHere] = useState<string | undefined>(undefined);

  const def: QuestDef | undefined = QUESTS.find((candidate) => candidate.id === questId);
  if (!def) return <NotFoundPage />;
  if (!profile) return <div />;

  const kingdom = KINGDOMS.find((candidate) => candidate.id === def.kingdomId);
  // Derived on every render, like mastery everywhere else: retention decays, so
  // a value memoised on the profile would go quietly out of date on the screen.
  const now = Date.now();

  /*
   * A quest belongs to an island, and a sealed island is not the student's yet
   * — the seal has always withheld the quest line first. So this route hands
   * back to the kingdom's own page, which states the seal and hands over the
   * concepts that break it. Nothing mathematical is refused here: a quest owns
   * no mathematics (`CLAUDE.md`), and every lesson it would have pointed at is
   * reachable from a concept page, a course and the Math Map as before.
   */
  if (kingdomSealed(profile, def.kingdomId, now)) {
    return <Navigate to={`/adventure/kingdoms/${def.kingdomId}`} replace />;
  }
  const progress: QuestProgress = questProgress(def, QUESTS, profile, now);
  const blocker = blockingQuest(def, QUESTS, profile);

  const skipped = storySkipped.get(def.id) === true;
  const toggleStory = (): void => {
    storySkipped.set(def.id, !skipped);
    redraw((tick) => tick + 1);
  };

  const onAccept = (): void => acceptQuest(def.id);

  const onClaim = (): void => {
    // The button is only rendered when the reward is waiting; this is the
    // second lock, because a stale render must not be able to ask twice. The
    // third and load-bearing one is `claimQuestReward` itself, which is
    // idempotent by `claimedQuestIds`.
    if (progress.state !== 'complete') return;
    claimQuestReward({ questId: def.id, xp: def.reward.xp, gold: def.reward.gold });
    /*
     * Celebration is rationed (CLAUDE.md). A quest claim is rare and earned, so
     * it gets a toast — keyed by quest id, so the queue de-duplicates it and no
     * amount of tapping can stack two. Confetti is held back for the main
     * chain: a side errand that showered the screen would spend the currency
     * that makes finishing a kingdom's story feel like something.
     */
    celebrate({
      key: `quest-claim-${def.id}`,
      tone: 'quest',
      icon: QUEST_KIND[def.kind].icon,
      title: `Quest complete — ${def.title}`,
      detail: `+${def.reward.gold} gold`,
      xp: def.reward.xp,
    });
    setClaimedHere(def.id);
  };

  const percent = progress.total === 0 ? 0 : Math.round((progress.done / progress.total) * 100);
  const read = new Set(profile.completedLessonIds);

  return (
    <div className="page page--narrow quest-page">
      {claimedHere === def.id && def.kind === 'main' ? <Confetti /> : null}

      <nav className="breadcrumb" aria-label="Breadcrumb">
        <Link to="/adventure/map">World map</Link>
        <span aria-hidden="true">/</span>
        {kingdom ? <Link to={`/adventure/kingdoms/${kingdom.id}`}>{kingdom.name}</Link> : <span>Quest</span>}
        <span aria-hidden="true">/</span>
        <span>{def.title}</span>
      </nav>

      <header className="page-header quest-page__header">
        <div className="quest-page__badges">
          <QuestKindBadge kind={def.kind} />
          <QuestStateChip progress={progress} blocker={blocker} />
        </div>
        <h1 className="page__title">{def.title}</h1>
        <p className="page__lead">
          Pays <strong>+{def.reward.xp} XP</strong> and <strong>+{def.reward.gold} gold</strong> when it is done.
        </p>
      </header>

      {/* ------------------------------------------------------- the story --- */}
      <section className="quest-story" aria-labelledby="quest-story-heading">
        <div className="quest-story__head">
          <h2 id="quest-story-heading" className="quest-story__heading">
            The story
          </h2>
          <Button variant="ghost" size="sm" onClick={toggleStory} aria-expanded={!skipped} aria-controls="quest-story-beats">
            {skipped ? 'Read the story' : 'Skip story'}
          </Button>
        </div>
        {skipped ? (
          <p className="quest-story__skipped">Story hidden. The mathematics below is the whole quest.</p>
        ) : (
          <ol className="quest-story__beats" id="quest-story-beats">
            {def.story.map((beat, index) => (
              <StoryPanel key={index} beat={beat} />
            ))}
          </ol>
        )}
      </section>

      {/* --------------------------------------------------- the objective --- */}
      <Card className={`quest-objective ${progress.done >= progress.total ? 'quest-objective--full' : ''}`}>
        <h2 className="quest-objective__heading">What this quest asks</h2>
        <p className="quest-objective__sentence">{objectiveSentence(def.objective)}</p>

        <div
          className="quest-objective__track"
          role="progressbar"
          aria-valuenow={progress.done}
          aria-valuemin={0}
          aria-valuemax={progress.total}
          aria-label={`${def.title}: ${progress.done} of ${progress.total}`}
        >
          <div className="quest-objective__fill" style={{ width: `${percent}%` }} />
        </div>
        <p className="quest-objective__count">
          {progress.done}/{progress.total} done
        </p>

        {progress.state === 'locked' ? (
          <p className="quest-objective__note">
            {blocker ? `${blocker.title} comes first in this kingdom's story.` : 'This quest is not open yet.'} Nothing
            below is held back by that — every lesson and every practice page here is open right now.
          </p>
        ) : null}

        {progress.state === 'available' ? (
          <>
            {def.objective.kind === 'practice' ? (
              <p className="quest-objective__note">
                Correct answers count from the moment you accept — work you did earlier belongs to whatever you were
                doing at the time.
              </p>
            ) : null}
            <Button onClick={onAccept}>Accept quest</Button>
          </>
        ) : null}

        {progress.state === 'complete' ? (
          <>
            <p className="quest-objective__note">
              Done. <span aria-hidden="true">🎁</span> The reward is waiting.
            </p>
            <Button onClick={onClaim}>
              Claim +{def.reward.xp} XP and +{def.reward.gold} gold
            </Button>
          </>
        ) : null}

        {progress.state === 'claimed' ? (
          <p className="quest-objective__paid">
            <span aria-hidden="true">✅</span> Claimed — you were paid +{def.reward.xp} XP and +{def.reward.gold} gold.
          </p>
        ) : null}
      </Card>

      {/* ---------------------------------------------------- the work --- */}
      <section className="dashboard-section quest-work" aria-labelledby="quest-work-heading">
        <h2 id="quest-work-heading" className="quest-work__heading">
          The work
        </h2>

        {def.objective.kind === 'practice' || def.objective.kind === 'master-concepts' ? (
          <>
            <ConceptTargets
              conceptIds={def.objective.conceptIds}
              showBand={def.objective.kind === 'master-concepts'}
              masteryOf={masteryOf}
            />
            <p className="quest-work__note">
              Practice these skills anywhere — the quest is watching. It counts what you answer on the ordinary practice
              pages, so nothing has to be done from here.
            </p>
          </>
        ) : null}

        {def.objective.kind === 'complete-lessons' ? (
          <>
            <LessonTargets lessonIds={def.objective.lessonIds} read={read} />
            <p className="quest-work__note">
              Read them anywhere — the quest is watching. A lesson finished from the map or your dashboard counts here
              just the same.
            </p>
          </>
        ) : null}

        {def.objective.kind === 'clear-quests' ? (
          <QuestTargets questIds={def.objective.questIds} profile={profile} now={now} />
        ) : null}
      </section>

      <div className="button-row">
        {kingdom ? (
          <Link to={`/adventure/kingdoms/${kingdom.id}`} className="btn btn--secondary">
            Back to {kingdom.name}
          </Link>
        ) : null}
        <Link to="/adventure/map" className="btn btn--secondary">
          World map
        </Link>
      </div>
    </div>
  );
}
